// React & Hooks
import { memo, useEffect, useRef, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Validation & Types
import { Errors } from "services/forms/validation/types";
import { validateVehicleStep } from "./validation";
import { VehicleEditProps } from "./types";

// API - Fleet Vehicles
import {
  createFleetVehicleAPI,
  getFleetVehicleAPI,
  publishFleetVehicleAPI,
  updateFleetVehicleAPI,
  getVehicleWizardStepsAPI,
} from "services/api/fleet/vehicles/api";
import { VEHICLE_WIZARD_STEP_LABELS } from "services/api/settings/fleet/consts";
import { FleetVehicle, FleetVehicleEditFormValues } from "services/api/fleet/vehicles/types";
import { initialFleetVehicleEditForm } from "services/api/fleet/vehicles/consts";

// UI Components
import { Modal, SuccessOverlay } from "ui/components/Modal";
import successAnimation from "ui/theme/animations/success.json";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Loader } from "ui/components/Loader";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import {
  FLEET_VEHICLE_NEW_PAGE_TITLE,
  FLEET_VEHICLE_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { transformFleetVehicleEditFormValues } from "services/api/fleet/vehicles/transformations";
import WizardStep from "ui/components/Wizard/WizardStep";
import { Wizard } from "ui/components/Wizard";
import VehicleStepStatusBar from "./components/VehicleStepStatusBar/VehicleStepStatusBar";
import VehiclePickerForm from "./components/VehiclePickerForm/VehiclePickerForm";
import VehicleInfoForm from "./components/VehicleInfoForm/VehicleInfoForm";
import VehiclePriceForm from "./components/VehiclePriceForm/VehiclePriceForm";
import VehicleLocationForm from "./components/VehicleLocationForm/VehicleLocationForm";
import VehiclePictureForm from "./components/VehiclePictureForm/VehiclePictureForm";
import VehicleDocumentForm from "./components/VehicleDocumentForm/VehicleDocumentForm";
import VehicleOwnerForm from "./components/VehicleOwnerForm/VehicleOwnerForm";
import VehicleFireExtinguisherForm from "./components/VehicleFireExtinguisherForm/VehicleFireExtinguisherForm";
import VehicleTireForm from "./components/VehicleTireForm/VehicleTireForm";
import VehicleEquipmentForm from "./components/VehicleEquipmentForm/VehicleEquipmentForm";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";
import { VehicleTireFormRef } from "./components/VehicleTireForm/types";
import { VehicleEquipmentFormRef } from "./components/VehicleEquipmentForm/types";
import { VehicleInfo } from "./components/VehicleSummaryCard/types";
import VehicleWizardIntro from "./components/VehicleWizardIntro/VehicleWizardIntro";
import { initialVehicleInfo } from "./consts";


const VehicleEditCard: React.FC<VehicleEditProps> = (props) => {
  const { vehicleId, show, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [stepCurrent, setStepCurrent] = useState(0);
  const [formValues, setFormValues] = useState<FleetVehicleEditFormValues>(
    initialFleetVehicleEditForm
  );
  const [vehicleInfo, setVehicleInfo] = useState<VehicleInfo>(initialVehicleInfo);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  // Uključeni koraci wizarda (po tenantu). Dok se ne učitaju, prikazuje se loader.
  const [enabledStepKeys, setEnabledStepKeys] = useState<string[]>([]);
  const [stepsLoaded, setStepsLoaded] = useState<boolean>(false);
  const tireFormRef = useRef<VehicleTireFormRef>(null);
  const equipmentFormRef = useRef<VehicleEquipmentFormRef>(null);
  const isProcessingRef = useRef(false);
  const finishedVehicleIdRef = useRef<string | null>(null);

  // Dohvat konfiguracije koraka pri svakom otvaranju modala.
  useEffect(() => {
    if (!show) {
      setStepsLoaded(false);
      return;
    }
    (async () => {
      try {
        const keys = await getVehicleWizardStepsAPI();
        setEnabledStepKeys(keys);
      } catch {
        // Fallback: svi koraci (ključevi iz kataloga labela, kanonski redoslijed).
        setEnabledStepKeys(Object.keys(VEHICLE_WIZARD_STEP_LABELS));
      } finally {
        setStepsLoaded(true);
      }
    })();
  }, [show]);

  useEffect(() => {
    if (show && vehicleId) {
      fetchVehicle(vehicleId);
    }
    if (show && !vehicleId) {
      setFormValues(initialFleetVehicleEditForm);
      setStepCurrent(0);
      setErrors({});
    }
  }, [show, vehicleId]);

  const fetchVehicle = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleEditForm);
      return;
    }

    setIsFetching(true);

    let result: FleetVehicle | undefined;
    try {
      result = await getFleetVehicleAPI(id);

      if (result) {
        const vehicle = result;
        setFormValues(transformFleetVehicleEditFormValues(vehicle));
        // Napuni summary "Naziv" (proizvođač/model/tip) iz učitanog vozila — picker izvodi ostale podatke tipa.
        setVehicleInfo((prev) => ({
          ...prev,
          vehicleManufacturerName: vehicle.vehicleManufacturer?.name ?? null,
          vehicleModelName: vehicle.vehicleModel?.name ?? null,
          vehicleTypeName: vehicle.vehicleType?.name ?? null,
          vehicleImage: vehicle.vehicleType?.image ?? prev.vehicleImage,
        }));
      }
    } catch {
      setIsFetching(false);
      return;
    }

    setIsFetching(false);

    setErrors({});
  };

  // Kanonski katalog koraka (obavezni prvi): ključ → render sadržaja koraka.
  // VAŽNO: redoslijed je picker, info, tires, equipment, ... (gume PRIJE opreme).
  const STEP_CATALOG: { key: string; render: () => JSX.Element }[] = [
    {
      key: "picker",
      render: () => (
        <div className="flex justify-center items-center mt-16 px-4 ">
          <div className="w-full max-w-4xl">
            <VehiclePickerForm
              formValues={formValues}
              setFormValues={setFormValues}
              errors={errors}
              setErrors={setErrors}
              vehicleInfo={vehicleInfo}
              setVehicleInfo={setVehicleInfo}
              showHeader
            />
          </div>
        </div>
      ),
    },
    {
      key: "info",
      render: () => (
        <VehicleInfoForm
          formValues={formValues}
          setFormValues={setFormValues}
          errors={errors}
          setVehicleInfo={setVehicleInfo}
        />
      ),
    },
    {
      key: "tires",
      render: () => (
        <VehicleTireForm
          ref={tireFormRef}
          formValues={formValues}
          setFormValues={setFormValues}
          setVehicleInfo={setVehicleInfo}
          errors={errors}
        />
      ),
    },
    {
      key: "location",
      render: () => (
        <VehicleLocationForm
          formValues={formValues}
          setFormValues={setFormValues}
          errors={errors}
        />
      ),
    },
    {
      key: "equipment",
      render: () => (
        <VehicleEquipmentForm
          ref={equipmentFormRef}
          formValues={formValues}
          vehicleInfo={vehicleInfo}
          setVehicleInfo={setVehicleInfo}
          errors={errors}
        />
      ),
    },
    {
      key: "extinguisher",
      render: () => (
        <VehicleFireExtinguisherForm
          formValues={formValues}
          setFormValues={setFormValues}
          setVehicleInfo={setVehicleInfo}
          vehicleInfo={vehicleInfo}
        />
      ),
    },
    {
      key: "owner",
      render: () => (
        <VehicleOwnerForm
          formValues={formValues}
          setFormValues={setFormValues}
          setVehicleInfo={setVehicleInfo}
          errors={errors}
        />
      ),
    },
    {
      key: "price",
      render: () => (
        <VehiclePriceForm
          formValues={formValues}
          setFormValues={setFormValues}
          setVehicleInfo={setVehicleInfo}
          errors={errors}
          setErrors={setErrors}
        />
      ),
    },
    {
      key: "pictures",
      render: () => (
        <VehiclePictureForm
          formValues={formValues}
          setFormValues={setFormValues}
          setVehicleInfo={setVehicleInfo}
          vehicleInfo={vehicleInfo}
        />
      ),
    },
    {
      key: "documents",
      render: () => (
        <VehicleDocumentForm
          formValues={formValues}
          setFormValues={setFormValues}
          setVehicleInfo={setVehicleInfo}
        />
      ),
    },
  ];

  // Aktivni koraci = katalog filtriran na uključene ključeve (kanonski redoslijed).
  const activeSteps = STEP_CATALOG.filter((s) => enabledStepKeys.includes(s.key));
  const activeStepLabels = activeSteps.map(
    (s) => VEHICLE_WIZARD_STEP_LABELS[s.key] ?? s.key
  );
  const currentStepKey = activeSteps[stepCurrent]?.key;

  // Obavezni koraci (uvijek prvi u katalogu). Nakon njih se otključava slobodna
  // navigacija na bilo koji korak u status-baru.
  const MANDATORY_STEP_KEYS = ["picker", "info"];
  const mandatoryCount = activeSteps.filter((s) =>
    MANDATORY_STEP_KEYS.includes(s.key)
  ).length;

  const handleNextStep = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      // Spremi odabranu/ručno dodanu opremu prije napuštanja koraka opreme.
      if (currentStepKey === "equipment" && equipmentFormRef.current) {
        const result = await equipmentFormRef.current.saveAll();
        if (result < 0) return; // greška pri spremanju
      }

      // Spremi inline izmjene guma prije validacije na koraku guma.
      if (currentStepKey === "tires" && tireFormRef.current) {
        const result = await tireFormRef.current.saveAll();
        if (result < 0) return; // greška pri spremanju
      }

      const validation = await validateVehicleStep(formValues, currentStepKey || "");
      if (!validation.isValid) {
        setErrors(validation.errors);
        return;
      }
      await saveVehicle();
      setStepCurrent((s) => s + 1);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleBackStep = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      await saveVehicle();
      setErrors({});
      setStepCurrent((s) => s - 1);
    } finally {
      isProcessingRef.current = false;
    }
  };

  /** Sprema vozilo bez validacije — validacija se radi u handleNextStep */
  const saveVehicle = async () => {
    setIsLoading(true);

    try {
      let result: any;
      const id = vehicleId || formValues.id;

      if (!!id) {
        result = await updateFleetVehicleAPI(id, formValues, t);
      } else {
        result = await createFleetVehicleAPI(formValues, t);
      }

      // Koristi response za update formValues umjesto ponovnog fetcha
      if (result?.id && !formValues.id) {
        setFormValues(prev => ({ ...prev, id: result.id }));
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const publishVehicle = async () => {
    setIsLoading(true);
    try {
      let result;
      const id = vehicleId || formValues.id;
      if (!!id) {
        result = await publishFleetVehicleAPI(id, formValues, t);
      }
      if (result && result.published && onSuccess) {
        const createdVehicleId = result.id || id;

        setShowSuccessOverlay(true);
        setIsLoading(false);
        finishedVehicleIdRef.current = createdVehicleId;
        return;
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  }


  /** Završna animacija gotova — zatvori modal i resetiraj stanje */
  const handleSuccessDismiss = () => {
    const createdVehicleId = finishedVehicleIdRef.current;
    setFormValues(initialFleetVehicleEditForm);
    setStepCurrent(0);
    setShowSuccessOverlay(false);
    finishedVehicleIdRef.current = null;
    if (createdVehicleId && onSuccess) onSuccess(createdVehicleId);
  };

  /** Potvrda zatvaranja modala — pita korisnika prije zatvaranja */
  const handleClose = () => {
    createDialog({
      onConfirm: onClose,
    });
  };

  const firstStepCompleted = !!formValues.id;

  // Klik na korak u traci sprema trenutni korak pa navigira (isto kao "Natrag").
  // Traka dopušta samo natrag/trenutni korak — naprijed ide kroz "Nastavi".
  const handleStepClick = async (stepIndex: number) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    try {
      await saveVehicle();
      setErrors({});
      setStepCurrent(stepIndex);
    } finally {
      isProcessingRef.current = false;
    }
  };

  const statusBar = (
    <VehicleStepStatusBar
      stepLabels={activeStepLabels}
      stepCurrent={stepCurrent}
      onStepClick={handleStepClick}
      firstStepCompleted={firstStepCompleted}
      mandatoryCount={mandatoryCount}
    />
  );

  const steps = activeSteps.map((step, index) => (
    <WizardStep key={step.key} stepNumber={index} stepCurrent={stepCurrent}>
      {statusBar}
      {step.render()}
    </WizardStep>
  ));

  return (
    <>
    <Modal
      show={show}
      onHide={handleClose}
      size="full"
      position="side"
      intro={{
        content: (
          <VehicleWizardIntro
            vehicleInfo={vehicleInfo}
            stepKey={currentStepKey}
            stepKeys={activeSteps.map((step) => step.key)}
          />
        ),
      }}
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={vehicleId
          ? FLEET_VEHICLE_EDIT_PAGE_TITLE.text
          : FLEET_VEHICLE_NEW_PAGE_TITLE.text
        }
        onHide={handleClose}
      />
      <ModalBody>
        {isFetching || !stepsLoaded ? (
          <div className="relative min-h-[60vh] xl:min-h-[600px] h-[calc(100vh-160px)]">
            <Loader isLoading={true}>{null}</Loader>
          </div>
        ) : (
          <Wizard steps={steps} />
        )}
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={publishVehicle}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
    <SuccessOverlay
      show={showSuccessOverlay}
      animation={successAnimation}
      title="Vozilo je uspješno kreirano!"
      description="Možete ga pregledati na stranici vozila ili kreirati novo."
      onDismiss={handleSuccessDismiss}
    />
    </>
  );
};
export default memo(VehicleEditCard);
